import { useEffect, FC, Suspense, lazy } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { InsightsWrapper } from "./Insights.styled";
import PageWrapper from "components/reusable/PageWrapper/PageWrapper";
import { GradientText } from "shared/shared_styled_comps/components.styled";
import { useSelector } from "react-redux";
import ForecastPage from "components/ForecastPage/ForecastPage";
// Lazy load components
const WinLossAnalysis = lazy(() => import("components/WinLossAnalysis/WinLossAnalysis"));
const SalesPrioritization = lazy(() => import("components/SalesPrioritization/SalesPrioritization"));
const MeddicSpiderChart = lazy(() => import("components/MeddicSpiderChart/MeddicSpiderChart"));
const ScoreChart = lazy(() => import("components/ScoreChart/ScoreChart"));
const WinningBehaviorsChart = lazy(() => import("components/WinningBehaviorsChart/WinningBehaviorsChart"));
// const StageTimingChart = lazy(() => import("components/StageTimingChart/StageTimingChart"));

interface InsightsProps {}

const Insights: FC<InsightsProps> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.auth.user);

  // URL mapping for each tab
  const tabRoutes = [
    "forecast",
    "win-loss-analysis", 
    "winning-behavior-prioritization",
    "analysis",
    "winning-behaviors-trends"
  ];

  // Get current active tab based on URL
  const getCurrentTab = (): number => {
    const pathSegments = location.pathname.split('/');
    const currentRoute = pathSegments[pathSegments.length - 1];
    const tabIndex = tabRoutes.indexOf(currentRoute);
    return tabIndex !== -1 ? tabIndex : 0; // Default to first tab if no match
  };

  const activeTab = getCurrentTab();

  // Handle default route redirect
  useEffect(() => {
    if (location.pathname === '/app/insights' || location.pathname === '/app/insights/') {
      navigate('/app/insights/forecast', { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    const tabsHeader = document.querySelector(".tabs-header") as HTMLElement;
    if (tabsHeader) {
      tabsHeader.style.setProperty("--active-tab-index", activeTab.toString());
    }
  }, [activeTab]);

  const tabs = [
    {
      label: "Forecast",
      content: <Suspense fallback={<div>Loading...</div>}><ForecastPage /></Suspense>,
    },
    { 
      label: "Win / Loss Analysis", 
      content: <Suspense fallback={<div>Loading...</div>}><WinLossAnalysis /></Suspense> 
    },
    {
      label: "Winning Behavior Prioritization",
      content: <Suspense fallback={<div>Loading...</div>}><SalesPrioritization /></Suspense>,
    },
    {
      label: `${user && user.default_method && user.default_method.title} Analysis`,
      content: (
        <Suspense fallback={<div>Loading...</div>}>
          <MeddicSpiderChart />
          <ScoreChart />
        </Suspense>
      ),
    },
    { 
      label: "Winning Behaviors Trends", 
      content: <Suspense fallback={<div>Loading...</div>}><WinningBehaviorsChart /></Suspense> 
    },
    // { label: "Pipeline Trends", content: <StageTimingChart /> },
  ];

  return (
    <PageWrapper pageTitle="Insights">
      <InsightsWrapper tabs={tabs.length}>
        <div className="tabs-header">
          {tabs.map((tab, index) => (
            <button
              key={index}
              className={`tab-button ${activeTab === index ? "active" : ""}`}
              onClick={() => navigate(`/app/insights/${tabRoutes[index]}`)}
            >
              {activeTab === index ? (
                <GradientText className="active-tab-content">
                  {tab.label}
                </GradientText>
              ) : (
                tab.label
              )}
            </button>
          ))}
        </div>
        <div className="tab-content">
          {tabs.map((tab, index) => (
            <div
              key={index}
              style={{ display: activeTab === index ? "block" : "none" }}
            >
              {activeTab === index ? tab.content : null}
            </div>
          ))}
        </div>
      </InsightsWrapper>
    </PageWrapper>
  );
};

export default Insights;
